import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Paper,
  Snackbar,
  Backdrop,
  LinearProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Upload as UploadIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  CheckBox as CheckBoxIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import {
  criarMonografia,
  resubmeterMonografiaAposCorrecoes
} from '../../services/monografiaService';
import { listarProjetos } from '../../services/projetoService';

// COMPONENTE DE NOTIFICAÇÃO CUSTOMIZADO
const NotificationSnackbar = ({ open, message, severity, onClose }) => {
  const getIcon = () => {
    switch (severity) {
      case 'success': return <CheckCircleIcon />;
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={8000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert 
        onClose={onClose} 
        severity={severity} 
        sx={{ 
          width: '100%',
          minWidth: '350px',
          fontSize: '14px',
          '& .MuiAlert-icon': {
            fontSize: '20px'
          }
        }}
        icon={getIcon()}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

// PropTypes para NotificationSnackbar
NotificationSnackbar.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  onClose: PropTypes.func.isRequired
};

// COMPONENTE PARA UPLOAD DE ARQUIVO COM PROPTYPES
const FileUploadSection = ({ 
  fieldName, 
  title, 
  acceptedTypes, 
  required = false, 
  description,
  formData,
  fileNames,
  loading,
  handleFileChange,
  handleClearFile
}) => (
  <Paper 
    variant="outlined" 
    sx={{ 
      p: 3, 
      border: formData[fieldName] ? '2px solid #4caf50' : '1px solid #e0e0e0',
      borderRadius: 2,
      transition: 'all 0.3s ease'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {required && (
        <Chip 
          label="Obrigatório" 
          size="small" 
          color="error" 
          sx={{ ml: 1 }} 
        />
      )}
      {formData[fieldName] && (
        <Chip 
          label="Anexado" 
          size="small" 
          color="success" 
          icon={<CheckBoxIcon />}
          sx={{ ml: 1 }} 
        />
      )}
    </Box>
    
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>
    )}

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Button
        variant={formData[fieldName] ? "outlined" : "contained"}
        component="label"
        startIcon={formData[fieldName] ? <CloudUploadIcon /> : <UploadIcon />}
        disabled={loading}
        color={formData[fieldName] ? "success" : "primary"}
      >
        {formData[fieldName] ? 'Trocar Arquivo' : `Selecionar ${acceptedTypes}`}
        <input
          type="file"
          hidden
          accept={acceptedTypes.includes('PDF') ? '.pdf' : acceptedTypes.includes('Word') ? '.doc,.docx' : '.pdf,.doc,.docx'}
          onChange={(e) => handleFileChange(e, fieldName)}
        />
      </Button>
      
      {fileNames[fieldName] && (
        <>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {fileNames[fieldName]}
            </Typography>
            {formData[fieldName] && (
              <Typography variant="caption" color="text.secondary">
                ({(formData[fieldName].size / (1024 * 1024)).toFixed(2)}MB)
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            onClick={() => handleClearFile(fieldName)}
            startIcon={<CancelIcon />}
            color="error"
          >
            Remover
          </Button>
        </>
      )}
    </Box>
  </Paper>
);

// PropTypes para FileUploadSection
FileUploadSection.propTypes = {
  fieldName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  acceptedTypes: PropTypes.string.isRequired,
  required: PropTypes.bool,
  description: PropTypes.string,
  formData: PropTypes.object.isRequired,
  fileNames: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  handleFileChange: PropTypes.func.isRequired,
  handleClearFile: PropTypes.func.isRequired
};

const MonografiaForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detectar se é resubmissão
  const isResubmissao = location.state?.resubmissao || false;
  const monografiaId = location.state?.monografiaId;
  const monografiaOriginal = location.state?.monografia;
  
  const [formData, setFormData] = useState({
    projetoId: monografiaOriginal?.projeto?.id || '',
    versaoDocumento: '',
    documentoMonografia: null,
    situacaoAcademica: null,
    situacaoFinanceira: null,
    cvSubmitido: null,
    biSubmitido: null,
    comprovativoPagamento: null
  });

  const [fileNames, setFileNames] = useState({
    documentoMonografia: '',
    situacaoAcademica: '',
    situacaoFinanceira: '',
    cvSubmitido: '',
    biSubmitido: '',
    comprovativoPagamento: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [projetos, setProjetos] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // ESTADOS PARA NOTIFICAÇÕES
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // FUNÇÃO PARA VOLTAR À PÁGINA ANTERIOR
  const handleGoBack = () => {
    showNotification('Voltando à página anterior...', 'info');
    navigate(-1);
  };

  // FUNÇÃO PARA IR PARA O DASHBOARD
  const handleGoToDashboard = () => {
    showNotification('Redirecionando para o dashboard...', 'info');
    navigate('/dashboard');
  };

  // EFEITO PARA REDIRECIONAMENTO AUTOMÁTICO APÓS SUCESSO
  useEffect(() => {
    let countdownTimer;
    
    if (success && isRedirecting) {
      countdownTimer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownTimer);
            handleGoToDashboard();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
    };
  }, [success, isRedirecting, navigate]);

  // FUNÇÃO PARA CANCELAR REDIRECIONAMENTO
  const handleCancelRedirect = () => {
    setIsRedirecting(false);
    setCountdown(5);
    showNotification('Redirecionamento cancelado. Você pode continuar na página.', 'info');
  };

  // FUNÇÃO PARA MOSTRAR NOTIFICAÇÕES
  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // FUNÇÃO PARA FECHAR NOTIFICAÇÕES
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // FUNÇÃO MELHORADA PARA TRATAMENTO DE ERROS
  const handleApiError = (error, operacao = 'operação') => {
    console.error(`Erro na ${operacao}:`, error);
    
    let mensagem = '';
    
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 400:
          if (data.message) {
            if (data.message.includes('arquivo') || data.message.includes('PDF') || data.message.includes('Word')) {
              mensagem = `${data.message}`;
            } else if (data.message.includes('tamanho') || data.message.includes('size')) {
              mensagem = `Arquivo muito grande! Tamanho máximo permitido: 10MB por arquivo`;
            } else if (data.message.includes('formato') || data.message.includes('type')) {
              mensagem = `Formato de arquivo inválido. Verifique os tipos permitidos para cada campo`;
            } else {
              mensagem = `${data.message}`;
            }
          } else {
            mensagem = `Dados inválidos. Verifique todos os campos obrigatórios`;
          }
          break;
        case 401:
          mensagem = `Sessão expirada. Por favor, faça login novamente`;
          break;
        case 403:
          mensagem = `Você não tem permissão para submeter monografias`;
          break;
        case 413:
          mensagem = `Um ou mais arquivos são muito grandes! Tamanho máximo: 10MB por arquivo`;
          break;
        case 422:
          if (data.errors && Array.isArray(data.errors)) {
            mensagem = `Erros de validação:\n${data.errors.map(err => `• ${err.message || err}`).join('\n')}`;
          } else if (data.message) {
            mensagem = `${data.message}`;
          } else {
            mensagem = `Dados inválidos. Verifique todos os campos`;
          }
          break;
        case 500:
          mensagem = `Erro interno do servidor. Tente novamente em alguns minutos`;
          break;
        default:
          mensagem = `Erro inesperado (${status}). Tente novamente`;
      }
    } else if (error.request) {
      mensagem = `Erro de conexão. Verifique sua internet e tente novamente`;
    } else {
      mensagem = `Erro inesperado: ${error.message}`;
    }
    
    showNotification(mensagem, 'error');
    return mensagem;
  };

  // VALIDAÇÃO DE ARQUIVOS MELHORADA
  const validateFile = (file, fieldName) => {
    if (!file) return true;

    // Verificar tamanho (10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showNotification(`O arquivo "${file.name}" é muito grande! Tamanho máximo: 10MB`, 'error');
      return false;
    }

    // Verificar tipos de arquivo por campo
    const allowedTypes = {
      documentoMonografia: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'],
      situacaoAcademica: ['application/pdf'],
      situacaoFinanceira: ['application/pdf'],
      cvSubmitido: ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'],
      biSubmitido: ['application/pdf'],
      comprovativoPagamento: ['application/pdf']
    };

    const allowed = allowedTypes[fieldName] || [];
    if (allowed.length > 0 && !allowed.includes(file.type)) {
      const typeNames = {
        'application/pdf': 'PDF',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (docx)',
        'application/msword': 'Word (doc)'
      };
      
      const allowedNames = allowed.map(type => typeNames[type] || type).join(', ');
      showNotification(`Tipo de arquivo inválido para "${fieldName}". Tipos permitidos: ${allowedNames}`, 'error');
      return false;
    }

    return true;
  };

  // Carregar projetos aprovados (apenas para submissão nova)
  useEffect(() => {
    const fetchProjetos = async () => {
      if (isResubmissao) {
        // Para resubmissão, não precisa carregar projetos
        return;
      }
      
      try {
        showNotification('Carregando projetos aprovados...', 'info');
        
        const response = await listarProjetos();
        const projetosAprovados = response.data.filter(p => p.status === 'APROVADO');
        setProjetos(projetosAprovados);
        
        if (projetosAprovados.length === 0) {
          showNotification('Nenhum projeto aprovado encontrado. Você precisa ter um projeto aprovado antes de submeter uma monografia.', 'warning');
        } else {
          showNotification(`${projetosAprovados.length} projetos aprovados carregados!`, 'success');
        }
      } catch (err) {
        console.error('Erro ao carregar projetos:', err);
        const errorMessage = handleApiError(err, 'carregamento dos projetos');
        setError(errorMessage);
      }
    };
    fetchProjetos();
  }, [isResubmissao]);

  // Lida com campos de texto
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Lida com upload de arquivos MELHORADO
  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      showNotification(`Validando arquivo "${file.name}"...`, 'info');
      
      // Validar arquivo
      if (!validateFile(file, fieldName)) {
        e.target.value = ''; // Limpar input
        return;
      }

      // Atualiza no state
      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
      
      // Salva o nome do arquivo
      setFileNames(prev => ({
        ...prev,
        [fieldName]: file.name
      }));

      const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      showNotification(`Arquivo "${file.name}" selecionado (${sizeInMB}MB)`, 'success');
    }
  };

  // Limpa um arquivo específico
  const handleClearFile = (fieldName) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: null
    }));
    setFileNames(prev => ({
      ...prev,
      [fieldName]: ''
    }));
    showNotification(`Arquivo removido do campo "${fieldName}"`, 'info');
  };

  // Reseta todo o form
  const resetForm = () => {
    setFormData({
      projetoId: isResubmissao ? (monografiaOriginal?.projeto?.id || '') : '',
      versaoDocumento: '',
      documentoMonografia: null,
      situacaoAcademica: null,
      situacaoFinanceira: null,
      cvSubmitido: null,
      biSubmitido: null,
      comprovativoPagamento: null
    });
    setFileNames({
      documentoMonografia: '',
      situacaoAcademica: '',
      situacaoFinanceira: '',
      cvSubmitido: '',
      biSubmitido: '',
      comprovativoPagamento: ''
    });
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
    showNotification('Formulário limpo com sucesso!', 'info');
  };

  // VALIDAÇÃO DO FORMULÁRIO
  const validateForm = () => {
    const errors = [];

    if (!isResubmissao && !formData.projetoId) {
      errors.push('Selecione um projeto aprovado');
    }

    if (!isResubmissao && !formData.versaoDocumento.trim()) {
      errors.push('Informe a versão do documento');
    }

    if (!formData.documentoMonografia) {
      errors.push('Anexe o documento principal da monografia');
    }

    if (errors.length > 0) {
      showNotification(`Campos obrigatórios:\n${errors.map(err => `• ${err}`).join('\n')}`, 'warning');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar formulário
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);

    showNotification(
      isResubmissao 
        ? 'Iniciando resubmissão da monografia...' 
        : 'Iniciando submissão da monografia...', 
      'info'
    );

    try {
      // Simular progresso de upload
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      if (isResubmissao) {
        // RESUBMISSÃO - usar função específica
        showNotification('Resubmetendo monografia corrigida...', 'info');
        
        await resubmeterMonografiaAposCorrecoes(monografiaId, formData.documentoMonografia);
        
        showNotification('Monografia resubmetida com sucesso! Aguarde nova análise do supervisor.', 'success');
      } else {
        // SUBMISSÃO NOVA - usar lógica existente
        const formDataObj = new FormData();
        formDataObj.append('projetoId', formData.projetoId);
        formDataObj.append('versaoDocumento', formData.versaoDocumento);

        // Adiciona arquivos obrigatórios
        if (formData.documentoMonografia) {
          formDataObj.append('documentoMonografia', formData.documentoMonografia);
        }

        // Adiciona arquivos opcionais apenas se existirem
        if (formData.situacaoAcademica) {
          formDataObj.append('situacaoAcademica', formData.situacaoAcademica);
        }
        if (formData.situacaoFinanceira) {
          formDataObj.append('situacaoFinanceira', formData.situacaoFinanceira);
        }
        if (formData.cvSubmitido) {
          formDataObj.append('cvSubmitido', formData.cvSubmitido);
        }
        if (formData.biSubmitido) {
          formDataObj.append('biSubmitido', formData.biSubmitido);
        }
        if (formData.comprovativoPagamento) {
          formDataObj.append('comprovativoPagamento', formData.comprovativoPagamento);
        }

        showNotification('Enviando arquivos para o servidor...', 'info');

        await criarMonografia(formDataObj);
        
        showNotification('Monografia submetida com sucesso! Aguarde a revisão pelos supervisores.', 'success');
      }

      // Finalizar progresso
      clearInterval(progressInterval);
      setUploadProgress(100);

      setSuccess(true);
      
      // Iniciar processo de redirecionamento automático
      setIsRedirecting(true);
      setCountdown(5);
      
      resetForm();
      if (onSuccess) onSuccess();

    } catch (err) {
      console.error('Erro ao submeter/resubmeter monografia:', err);
      handleApiError(err, isResubmissao ? 'resubmissão da monografia' : 'submissão da monografia');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  // VERIFICAR SE TODOS OS CAMPOS OBRIGATÓRIOS ESTÃO PREENCHIDOS
  const isFormValid = isResubmissao 
    ? formData.documentoMonografia // Para resubmissão, só precisa do documento
    : formData.projetoId && formData.versaoDocumento.trim() && formData.documentoMonografia;
    
  const attachedFilesCount = Object.values(formData).filter(value => value && typeof value === 'object').length;

  return (
    <Card sx={{ maxWidth: 1000, mx: 'auto' }}>
      <CardContent>
        {/* Cabeçalho com botão de voltar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DescriptionIcon color="primary" fontSize="large" />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {isResubmissao ? 'Resubmissão de Monografia' : 'Submissão de Monografia'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            Voltar
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Instruções importantes:</strong><br />
            {isResubmissao ? (
              <>
                • Anexe apenas o documento principal corrigido (obrigatório)<br />
                • Os demais documentos são opcionais para resubmissão<br />
                • O documento deve estar em formato Word (.doc ou .docx)<br />
              </>
            ) : (
              <>
                • Certifique-se de que seu projecto foi aprovado antes de submeter a monografia<br />
                • O documento principal deve estar em formato Word (.doc ou .docx)<br />
                • Outros documentos devem estar em formato PDF<br />
              </>
            )}
            • Tamanho máximo por arquivo: 10MB
          </Typography>
        </Alert>

        {/* Alert específico para resubmissão */}
        {isResubmissao && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Resubmissão de Monografia:</strong><br />
              • Título: {monografiaOriginal?.projeto?.proposta?.titulo || monografiaOriginal?.titulo}<br />
              • Versão atual: {monografiaOriginal?.versao_atual}<br />
              • Anexe apenas o documento principal corrigido (obrigatório)<br />
              • Os demais documentos são opcionais para resubmissão
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }} 
            icon={<CheckCircleIcon />}
            action={
              isRedirecting ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Redirecionando em {countdown}s
                  </Typography>
                  <Button color="inherit" size="small" onClick={handleCancelRedirect}>
                    Cancelar
                  </Button>
                  <Button color="inherit" size="small" onClick={handleGoToDashboard}>
                    Ir Agora
                  </Button>
                </Box>
              ) : (
                <Button color="inherit" size="small" onClick={handleGoToDashboard}>
                  Ir para Dashboard
                </Button>
              )
            }
          >
            {isResubmissao 
              ? 'Monografia resubmetida com sucesso! Receberá notificações sobre o andamento da nova análise.'
              : 'Monografia submetida com sucesso! Receberá notificações sobre o andamento da revisão.'
            }
            {isRedirecting && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress variant="determinate" value={(5 - countdown) * 20} />
              </Box>
            )}
          </Alert>
        )}

        {/* Indicador de Progresso */}
        {attachedFilesCount > 0 && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {attachedFilesCount} arquivo(s) anexado(s)
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(attachedFilesCount / 6) * 100} 
              sx={{ bgcolor: 'primary.dark', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }}
            />
          </Paper>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {/* Seleção de Projeto (apenas para submissão nova) */}
          {!isResubmissao && (
            <FormControl fullWidth required>
              <InputLabel>Projeto Aprovado</InputLabel>
              <Select
                name="projetoId"
                value={formData.projetoId}
                onChange={handleChange}
                label="Projeto Aprovado"
              >
                <MenuItem value="">Selecione um projecto aprovado</MenuItem>
                {projetos.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.proposta?.titulo || `Projeto #${p.id}`}
                    <Chip label="Aprovado" size="small" color="success" sx={{ ml: 1 }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Versão do Documento (apenas para submissão nova) */}
          {!isResubmissao && (
            <TextField
              fullWidth
              label="Versão do Documento"
              name="versaoDocumento"
              value={formData.versaoDocumento}
              onChange={handleChange}
              required
              placeholder="Ex: 1.0, Versão Final, ou Rev. 2"
              helperText="Informe a versão ou revisão do documento da monografia"
            />
          )}

          <Divider sx={{ my: 2 }}>
            <Chip label="Documentos da Monografia" color="primary" />
          </Divider>

          {/* Arquivo principal: Monografia */}
          <FileUploadSection
            fieldName="documentoMonografia"
            title="Documento Principal da Monografia"
            acceptedTypes="Word"
            required={true}
            description={isResubmissao 
              ? "Documento principal corrigido da monografia em formato Word (.doc ou .docx)"
              : "Documento principal da monografia em formato Word (.doc ou .docx)"
            }
            formData={formData}
            fileNames={fileNames}
            loading={loading}
            handleFileChange={handleFileChange}
            handleClearFile={handleClearFile}
          />

          {/* Outros documentos (apenas para submissão nova ou opcionais para resubmissão) */}
          {!isResubmissao && (
            <>
              <FileUploadSection
                fieldName="situacaoAcademica"
                title="Situação Acadêmica"
                acceptedTypes="PDF"
                description="Documento comprovativo da situação acadêmica atual"
                formData={formData}
                fileNames={fileNames}
                loading={loading}
                handleFileChange={handleFileChange}
                handleClearFile={handleClearFile}
              />

              <FileUploadSection
                fieldName="situacaoFinanceira"
                title="Situação Financeira"
                acceptedTypes="PDF"
                description="Comprovativo da situação financeira (propina em dia, etc.)"
                formData={formData}
                fileNames={fileNames}
                loading={loading}
                handleFileChange={handleFileChange}
                handleClearFile={handleClearFile}
              />

              <FileUploadSection
                fieldName="cvSubmitido"
                title="CV do Estudante"
                acceptedTypes="PDF ou Word"
                description="Curriculum Vitae atualizado do estudante"
                formData={formData}
                fileNames={fileNames}
                loading={loading}
                handleFileChange={handleFileChange}
                handleClearFile={handleClearFile}
              />

              <FileUploadSection
                fieldName="biSubmitido"
                title="Cópia do BI"
                acceptedTypes="PDF"
                description="Cópia digitalizada do Bilhete de Identidade"
                formData={formData}
                fileNames={fileNames}
                loading={loading}
                handleFileChange={handleFileChange}
                handleClearFile={handleClearFile}
              />

              <FileUploadSection
                fieldName="comprovativoPagamento"
                title="Comprovativo de Pagamento"
                acceptedTypes="PDF"
                description="Comprovativo de pagamento das taxas de monografia"
                formData={formData}
                fileNames={fileNames}
                loading={loading}
                handleFileChange={handleFileChange}
                handleClearFile={handleClearFile}
              />
            </>
          )}

          {/* Progresso de Upload */}
          {loading && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {isResubmissao ? 'Resubmetendo monografia...' : 'Enviando monografia...'} {uploadProgress.toFixed(0)}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Paper>
          )}

          {/* Lista de Verificação */}
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Lista de Verificação:
            </Typography>
            <List dense>
              {!isResubmissao && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      {formData.projetoId ? <CheckBoxIcon color="success" /> : <CancelIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText primary="Projeto aprovado selecionado" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {formData.versaoDocumento.trim() ? <CheckBoxIcon color="success" /> : <CancelIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText primary="Versão do documento informada" />
                  </ListItem>
                </>
              )}
              <ListItem>
                <ListItemIcon>
                  {formData.documentoMonografia ? <CheckBoxIcon color="success" /> : <CancelIcon color="error" />}
                </ListItemIcon>
                <ListItemText primary={isResubmissao ? "Documento corrigido anexado" : "Documento principal anexado"} />
              </ListItem>
              {isResubmissao && (
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="info" />
                  </ListItemIcon>
                  <ListItemText primary="Esta é uma resubmissão após correções solicitadas" />
                </ListItem>
              )}
            </List>
          </Paper>

          {/* Botões de Ação */}
          <Box
            sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', mt: 3, flexWrap: 'wrap' }}
          >
            {/* Botões à esquerda */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                disabled={loading}
                size="large"
              >
                Voltar
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={handleGoToDashboard}
                disabled={loading}
                size="large"
              >
                Dashboard
              </Button>
            </Box>

            {/* Botões à direita */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="button"
                onClick={resetForm}
                disabled={loading}
                startIcon={<ClearIcon />}
                size="large"
              >
                Limpar
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={loading || !isFormValid}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
                }
                size="large"
                sx={{ minWidth: 200 }}
              >
                {loading 
                  ? (isResubmissao ? 'Resubmetendo...' : 'Submetendo...') 
                  : (isResubmissao ? 'Resubmeter Monografia' : 'Submeter Monografia')
                }
              </Button>
            </Box>
          </Box>
        </Box>
      </CardContent>

      {/* BACKDROP PARA LOADING */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6">
            {isResubmissao ? 'Resubmetendo Monografia...' : 'Submetendo Monografia...'}
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 300 }}>
            Por favor, não feche esta página. O upload pode levar alguns minutos dependendo do tamanho dos arquivos.
          </Typography>
          <Box sx={{ width: 300, mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
              {uploadProgress.toFixed(0)}% concluído
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      {/* SISTEMA DE NOTIFICAÇÕES */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      />
    </Card>
  );
};

MonografiaForm.propTypes = {
  onSuccess: PropTypes.func
};

export default MonografiaForm;